import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  Cloud, 
  CheckCircle2, 
  XCircle, 
  Settings, 
  HardDrive,
  RefreshCw,
  ExternalLink,
  AlertTriangle,
  Info,
  DollarSign,
  Save,
  Loader2,
  Unplug,
  Mail,
  FolderOpen
} from 'lucide-react';
import MainLayout from '@/components/MainLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Configuracion({ user }) {
  // Estados para Google Drive
  const [driveConfigured, setDriveConfigured] = useState(false);
  const [driveConnected, setDriveConnected] = useState(false);
  const [driveStatus, setDriveStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [googleEmail, setGoogleEmail] = useState(null);

  // Estados para Tarifas
  const [tarifas, setTarifas] = useState({
    precio_tm_cortero: 0,
    precio_tm_boyero: 0,
    precio_tm_alzada_coyol: 0,
    precio_saco_coyolero: 0,
    precio_hora_peon: 0,
  });
  const [tarifasLoading, setTarifasLoading] = useState(true);
  const [savingTarifas, setSavingTarifas] = useState(false);
  const [tarifasModified, setTarifasModified] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    checkDriveStatus();
    loadTarifas();
    
    // Verificar si venimos del callback de OAuth
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('drive_connected') === 'true') {
      toast.success('¡Google Drive conectado exitosamente!');
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    if (urlParams.get('drive_error')) {
      toast.error(`Error al conectar Google Drive: ${urlParams.get('drive_error')}`);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const checkDriveStatus = async () => {
    setLoading(true);
    try {
      const configResponse = await axios.get(`${API}/drive/config-status`);
      setDriveConfigured(configResponse.data.configured);

      if (configResponse.data.configured && user?.id) {
        const statusResponse = await axios.get(`${API}/drive/status?user_id=${user.id}`);
        setDriveConnected(statusResponse.data.connected);
        setDriveStatus(statusResponse.data);
        setGoogleEmail(statusResponse.data.google_email || null);
      }
    } catch (error) {
      console.error('Error verificando estado de Drive:', error);
    } finally {
      setLoading(false);
    }
  };

  const connectGoogleDrive = async () => {
    if (!user?.id) {
      toast.error('Error: Usuario no identificado');
      return;
    }

    setConnecting(true);
    try {
      const response = await axios.get(`${API}/drive/connect?user_id=${user.id}`);
      
      if (response.data.authorization_url) {
        window.location.href = response.data.authorization_url;
      }
    } catch (error) {
      console.error('Error conectando Drive:', error);
      toast.error('Error al iniciar conexión con Google Drive');
      setConnecting(false);
    }
  };

  const disconnectGoogleDrive = async () => {
    if (!window.confirm('¿Estás seguro de desconectar Google Drive?\n\nLas imágenes ya subidas NO se eliminarán, pero las nuevas imágenes no se podrán guardar hasta que conectes otra cuenta.')) {
      return;
    }

    setDisconnecting(true);
    try {
      await axios.post(`${API}/drive/disconnect?user_id=${user?.id}`);
      toast.success('Google Drive desconectado correctamente');
      setDriveConnected(false);
      setGoogleEmail(null);
      setDriveStatus(null);
    } catch (error) {
      console.error('Error desconectando Drive:', error);
      toast.error('Error al desconectar Google Drive');
    } finally {
      setDisconnecting(false);
    }
  };

  // Funciones para Tarifas
  const loadTarifas = async () => {
    setTarifasLoading(true);
    try {
      const response = await axios.get(`${API}/tarifas`);
      setTarifas({
        precio_tm_cortero: response.data.precio_tm_cortero || 0,
        precio_tm_boyero: response.data.precio_tm_boyero || 0,
        precio_tm_alzada_coyol: response.data.precio_tm_alzada_coyol || 0,
        precio_saco_coyolero: response.data.precio_saco_coyolero || 0,
        precio_hora_peon: response.data.precio_hora_peon || 0,
      });
      if (response.data.updated_at) {
        setLastUpdated({
          fecha: new Date(response.data.updated_at).toLocaleString('es-CR'),
          usuario: response.data.updated_by || 'Desconocido'
        });
      }
    } catch (error) {
      console.error('Error cargando tarifas:', error);
      toast.error('Error al cargar las tarifas');
    } finally {
      setTarifasLoading(false);
    }
  };

  const handleTarifaChange = (field, value) => {
    const numValue = parseFloat(value) || 0;
    setTarifas(prev => ({ ...prev, [field]: numValue }));
    setTarifasModified(true);
  };

  const saveTarifas = async () => {
    setSavingTarifas(true);
    try {
      const response = await axios.put(`${API}/tarifas`, tarifas);
      if (response.data.success) {
        toast.success('Tarifas guardadas correctamente');
        setTarifasModified(false);
        setLastUpdated({
          fecha: new Date().toLocaleString('es-CR'),
          usuario: user?.username || 'admin'
        });
      }
    } catch (error) {
      console.error('Error guardando tarifas:', error);
      toast.error('Error al guardar las tarifas');
    } finally {
      setSavingTarifas(false);
    }
  };

  return (
    <MainLayout user={user} currentModule="configuracion">
      <div className="max-w-4xl mx-auto p-6">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <Settings className="w-8 h-8 mr-3 text-emerald-600" />
            Configuración del Sistema
          </h1>
          <p className="text-gray-600 mt-2">
            Gestiona las integraciones y configuraciones de tu aplicación
          </p>
        </div>

        <Tabs defaultValue="integraciones" className="space-y-6">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="integraciones" className="flex items-center gap-2">
              <Cloud className="w-4 h-4" />
              Integraciones
            </TabsTrigger>
            <TabsTrigger value="tarifas" className="flex items-center gap-2">
              <DollarSign className="w-4 h-4" />
              Tarifas
            </TabsTrigger>
          </TabsList>

          {/* TAB: Integraciones (Google Drive) */}
          <TabsContent value="integraciones">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <HardDrive className="w-6 h-6 mr-2 text-blue-600" />
                  Almacenamiento en la Nube - Google Drive
                </CardTitle>
                <CardDescription>
                  Conecta una cuenta de Google Drive para almacenar las imágenes de boletas y documentos de forma permanente.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
                    <span className="ml-2 text-gray-500">Verificando estado...</span>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Explicación de para qué sirve */}
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-start">
                        <Info className="w-5 h-5 text-blue-600 mr-3 flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-blue-800">
                          <p className="font-semibold mb-2">¿Para qué sirve Google Drive?</p>
                          <p className="mb-2">
                            Esta cuenta de Google Drive se utiliza como <strong>almacenamiento central</strong> para todas las imágenes 
                            y documentos de la aplicación. Al conectar una cuenta:
                          </p>
                          <ul className="list-disc list-inside space-y-1 ml-2">
                            <li><strong>Las fotos de boletas</strong> se guardan de forma permanente (no se pierden con actualizaciones)</li>
                            <li><strong>Las imágenes de WhatsApp</strong> se envían directamente desde Drive</li>
                            <li>Todos los usuarios de la app comparten el mismo almacenamiento</li>
                            <li>Los archivos se organizan automáticamente en carpetas (CDP_Archivos/boletas)</li>
                          </ul>
                          <p className="mt-2 text-xs text-blue-600">
                            <strong>Recomendación:</strong> Usa una cuenta de Google dedicada para la empresa, no una personal.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Estado de configuración del sistema */}
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center">
                        <Cloud className="w-5 h-5 mr-3 text-gray-600" />
                        <div>
                          <p className="font-medium text-gray-800">Configuración del Servidor</p>
                          <p className="text-sm text-gray-500">
                            Credenciales de Google API en el servidor
                          </p>
                        </div>
                      </div>
                      {driveConfigured ? (
                        <Badge className="bg-green-100 text-green-800 border-green-200">
                          <CheckCircle2 className="w-4 h-4 mr-1" />
                          Configurado
                        </Badge>
                      ) : (
                        <Badge className="bg-red-100 text-red-800 border-red-200">
                          <XCircle className="w-4 h-4 mr-1" />
                          No configurado
                        </Badge>
                      )}
                    </div>

                    {/* Estado de conexión - cuenta conectada */}
                    {driveConfigured && (
                      <div className={`p-4 rounded-lg border ${driveConnected ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <HardDrive className={`w-5 h-5 mr-3 ${driveConnected ? 'text-green-600' : 'text-yellow-600'}`} />
                            <div>
                              <p className="font-medium text-gray-800">
                                {driveConnected ? 'Cuenta de Google Drive Conectada' : 'Sin cuenta conectada'}
                              </p>
                              {driveConnected && googleEmail && (
                                <div className="flex items-center mt-1 text-sm text-gray-600">
                                  <Mail className="w-4 h-4 mr-1" />
                                  <span className="font-medium">{googleEmail}</span>
                                </div>
                              )}
                              {driveConnected && !googleEmail && (
                                <p className="text-sm text-gray-500">
                                  Cuenta conectada (email no disponible)
                                </p>
                              )}
                              {!driveConnected && (
                                <p className="text-sm text-yellow-700">
                                  Conecta una cuenta para habilitar el almacenamiento de imágenes
                                </p>
                              )}
                            </div>
                          </div>
                          {driveConnected ? (
                            <Badge className="bg-green-100 text-green-800 border-green-200">
                              <CheckCircle2 className="w-4 h-4 mr-1" />
                              Activo
                            </Badge>
                          ) : (
                            <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">
                              <AlertTriangle className="w-4 h-4 mr-1" />
                              Sin conectar
                            </Badge>
                          )}
                        </div>
                        
                        {/* Info adicional cuando está conectado */}
                        {driveConnected && (
                          <div className="mt-3 pt-3 border-t border-green-200">
                            <div className="flex items-center text-sm text-green-700">
                              <FolderOpen className="w-4 h-4 mr-2" />
                              <span>Los archivos se guardan en: <strong>Google Drive → CDP_Archivos</strong></span>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Botones de acción */}
                    {driveConfigured && (
                      <div className="flex flex-wrap gap-3 pt-4 border-t">
                        {driveConnected ? (
                          <>
                            <Button
                              variant="outline"
                              onClick={checkDriveStatus}
                              className="border-gray-300"
                            >
                              <RefreshCw className="w-4 h-4 mr-2" />
                              Verificar conexión
                            </Button>
                            <Button
                              variant="outline"
                              onClick={disconnectGoogleDrive}
                              disabled={disconnecting}
                              className="border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                            >
                              {disconnecting ? (
                                <>
                                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                  Desconectando...
                                </>
                              ) : (
                                <>
                                  <Unplug className="w-4 h-4 mr-2" />
                                  Desconectar cuenta
                                </>
                              )}
                            </Button>
                            <Button
                              onClick={connectGoogleDrive}
                              disabled={connecting}
                              className="bg-blue-600 hover:bg-blue-700"
                            >
                              {connecting ? (
                                <>
                                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                  Conectando...
                                </>
                              ) : (
                                <>
                                  <ExternalLink className="w-4 h-4 mr-2" />
                                  Cambiar cuenta
                                </>
                              )}
                            </Button>
                          </>
                        ) : (
                          <Button
                            onClick={connectGoogleDrive}
                            disabled={connecting}
                            className="bg-blue-600 hover:bg-blue-700"
                          >
                            {connecting ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Conectando...
                              </>
                            ) : (
                              <>
                                <ExternalLink className="w-4 h-4 mr-2" />
                                Conectar cuenta de Google Drive
                              </>
                            )}
                          </Button>
                        )}
                      </div>
                    )}

                    {/* Mensaje si no está configurado en el servidor */}
                    {!driveConfigured && (
                      <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <div className="flex items-start">
                          <AlertTriangle className="w-5 h-5 text-yellow-600 mr-3 flex-shrink-0 mt-0.5" />
                          <div className="text-sm text-yellow-800">
                            <p className="font-semibold">Google Drive no está configurado en el servidor</p>
                            <p>
                              Las credenciales de Google API no están configuradas. 
                              Contacta al administrador del sistema para habilitar esta funcionalidad.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* TAB: Tarifas */}
          <TabsContent value="tarifas">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <DollarSign className="w-6 h-6 mr-2 text-emerald-600" />
                  Tarifas Globales
                </CardTitle>
                <CardDescription>
                  Configura los precios base para el cálculo de planilla. 
                  Estos valores se usan para trabajos con precios fijos. Los trabajos por contrato 
                  (poda, rodajeada, etc.) se registran con su precio al momento de registrar la labor.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {tarifasLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
                    <span className="ml-2 text-gray-500">Cargando tarifas...</span>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Última actualización */}
                    {lastUpdated && (
                      <div className="p-3 bg-gray-50 rounded-lg text-sm text-gray-600">
                        <span className="font-medium">Última actualización:</span>{' '}
                        {lastUpdated.fecha} por {lastUpdated.usuario}
                      </div>
                    )}

                    {/* Campos de tarifas */}
                    <div className="grid gap-6 md:grid-cols-2">
                      {/* Precio TM/Cortero */}
                      <div className="space-y-2">
                        <Label htmlFor="precio_tm_cortero" className="flex items-center gap-2">
                          <span className="text-lg">🌴</span>
                          Precio TM / Cortero
                        </Label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">₡</span>
                          <Input
                            id="precio_tm_cortero"
                            type="number"
                            step="0.01"
                            min="0"
                            value={tarifas.precio_tm_cortero}
                            onChange={(e) => handleTarifaChange('precio_tm_cortero', e.target.value)}
                            className="pl-8"
                            placeholder="0.00"
                          />
                        </div>
                        <p className="text-xs text-gray-500">Precio por tonelada métrica para cortero</p>
                      </div>

                      {/* Precio TM/Boyero */}
                      <div className="space-y-2">
                        <Label htmlFor="precio_tm_boyero" className="flex items-center gap-2">
                          <span className="text-lg">🚜</span>
                          Precio TM / Boyero
                        </Label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">₡</span>
                          <Input
                            id="precio_tm_boyero"
                            type="number"
                            step="0.01"
                            min="0"
                            value={tarifas.precio_tm_boyero}
                            onChange={(e) => handleTarifaChange('precio_tm_boyero', e.target.value)}
                            className="pl-8"
                            placeholder="0.00"
                          />
                        </div>
                        <p className="text-xs text-gray-500">Precio por tonelada métrica para boyero</p>
                      </div>

                      {/* Precio TM/Alzada Coyol */}
                      <div className="space-y-2">
                        <Label htmlFor="precio_tm_alzada_coyol" className="flex items-center gap-2">
                          <span className="text-lg">🏋️</span>
                          Precio TM / Alzada Coyol
                        </Label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">₡</span>
                          <Input
                            id="precio_tm_alzada_coyol"
                            type="number"
                            step="0.01"
                            min="0"
                            value={tarifas.precio_tm_alzada_coyol}
                            onChange={(e) => handleTarifaChange('precio_tm_alzada_coyol', e.target.value)}
                            className="pl-8"
                            placeholder="0.00"
                          />
                        </div>
                        <p className="text-xs text-gray-500">Precio por tonelada métrica para alzada de coyol</p>
                      </div>

                      {/* Precio Saco/Coyolero */}
                      <div className="space-y-2">
                        <Label htmlFor="precio_saco_coyolero" className="flex items-center gap-2">
                          <span className="text-lg">🛍️</span>
                          Precio Saco / Coyolero
                        </Label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">₡</span>
                          <Input
                            id="precio_saco_coyolero"
                            type="number"
                            step="0.01"
                            min="0"
                            value={tarifas.precio_saco_coyolero}
                            onChange={(e) => handleTarifaChange('precio_saco_coyolero', e.target.value)}
                            className="pl-8"
                            placeholder="0.00"
                          />
                        </div>
                        <p className="text-xs text-gray-500">Precio por saco para coyolero</p>
                      </div>

                      {/* Precio Hora/Peón agrícola - ocupa toda la fila */}
                      <div className="space-y-2 md:col-span-2">
                        <Label htmlFor="precio_hora_peon" className="flex items-center gap-2">
                          <span className="text-lg">👷</span>
                          Precio Hora / Peón Agrícola
                        </Label>
                        <div className="relative max-w-md">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">₡</span>
                          <Input
                            id="precio_hora_peon"
                            type="number"
                            step="0.01"
                            min="0"
                            value={tarifas.precio_hora_peon}
                            onChange={(e) => handleTarifaChange('precio_hora_peon', e.target.value)}
                            className="pl-8"
                            placeholder="0.00"
                          />
                        </div>
                        <p className="text-xs text-gray-500">Precio por hora para peón agrícola</p>
                      </div>
                    </div>

                    {/* Nota informativa */}
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="flex items-start">
                        <Info className="w-5 h-5 text-amber-600 mr-3 flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-amber-800">
                          <p className="font-semibold mb-1">Nota sobre trabajos por contrato</p>
                          <p>
                            Los trabajos por contrato como poda, rodajeada, chapia, etc., tienen precios variables 
                            según las condiciones (edad, altura, suciedad de la mata). El precio se definirá 
                            al momento de registrar cada labor en el módulo de Reporte de Labores.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Botón guardar */}
                    <div className="flex justify-end pt-4 border-t">
                      <Button
                        onClick={saveTarifas}
                        disabled={savingTarifas || !tarifasModified}
                        className="bg-emerald-600 hover:bg-emerald-700"
                      >
                        {savingTarifas ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Guardando...
                          </>
                        ) : (
                          <>
                            <Save className="w-4 h-4 mr-2" />
                            Guardar Tarifas
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </MainLayout>
  );
}
